import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import { useStyle } from '../../Style'
import { formatDateLocale, canAccess } from '../../../../../../../lib/utils.es6'
import SkeletonWrapper from '../../../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import {
  getRealPayContract,
  extendRealPaySchedule,
  retryRealPayInstalment
} from '../../../../../../../lib/requests.es6'
import {
  getInstalmentStatusMeta,
  getContractStatusMeta
} from './instalmentStatus'
import { mockClient, mockContract, mockInstalments } from './mockData'

const money = amount =>
  typeof amount === 'number'
    ? amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : 'N/A'

const ACCOUNT_TYPE_LABEL = { 1: 'Cheque', 2: 'Savings' }

const StatusPill = ({ classes, label, pillClass }) => (
  <li className={classes[pillClass]}>
    <span>{label}</span>
  </li>
)

const RealPayContract = props => {
  const classes = useStyle()
  const policyGuid = props.policy && props.policy.guid

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMessage, setActionMessage] = useState(null)

  const fetchData = useCallback(() => {
    if (!policyGuid) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    getRealPayContract(policyGuid)
      .then(response => {
        setData(response.data && response.data.data)
      })
      .catch(err => {
        setError(
          (err.response && err.response.data && err.response.data.message) ||
            'Could not reach RealPay for this policy.'
        )
      })
      .finally(() => setLoading(false))
  }, [policyGuid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleExtend = () => {
    if (
      !window.confirm(
        'Extend this contract to 24 months of runway on RealPay? This creates real future debit-order instalments.'
      )
    ) {
      return
    }
    setActionLoading(true)
    setActionMessage(null)
    extendRealPaySchedule(policyGuid)
      .then(response => {
        const result = response.data && response.data.data
        setActionMessage({
          type: 'success',
          text:
            result && result.months_added
              ? `Added ${result.months_added} instalment(s).`
              : (result && result.message) || 'Schedule already up to date.'
        })
        fetchData()
      })
      .catch(err => {
        setActionMessage({
          type: 'error',
          text:
            (err.response && err.response.data && err.response.data.message) ||
            'Failed to extend schedule.'
        })
      })
      .finally(() => setActionLoading(false))
  }

  const handleRetryInstalment = instalment => {
    if (
      !window.confirm(
        `Retry the missed instalment on ${formatDateLocale(
          instalment.InstalmentActionDate
        )} for ${money(
          instalment.InstalmentAmount
        )}? Only do this once the customer has confirmed they want it collected.`
      )
    ) {
      return
    }
    setActionLoading(true)
    setActionMessage(null)
    retryRealPayInstalment(policyGuid, instalment.InstalmentReferenceNumber)
      .then(() => {
        setActionMessage({
          type: 'success',
          text: 'Retry submitted to RealPay.'
        })
        fetchData()
      })
      .catch(err => {
        setActionMessage({
          type: 'error',
          text:
            (err.response && err.response.data && err.response.data.message) ||
            'Failed to retry instalment.'
        })
      })
      .finally(() => setActionLoading(false))
  }

  const usingSampleData = !data
  const client = (data && data.client) || mockClient
  const contract = (data && data.contract) || mockContract
  const instalments = (data && data.instalments) || mockInstalments

  const contractStatus = getContractStatusMeta(contract.ContractStatus)

  const now = new Date()
  const withDates = instalments
    .map(i => ({
      ...i,
      _date: new Date(i.InstalmentActionDate.replace(' ', 'T'))
    }))
    .sort((a, b) => a._date - b._date)

  const collected = withDates.filter(i => i.InstalmentStatus === 'S')
  const scheduled = withDates.filter(i => i.InstalmentStatus === 'A')
  const needsAttention = withDates.filter(
    i => i.InstalmentStatus === 'R' || i.InstalmentStatus === 'W'
  )
  const cancelled = withDates.filter(i => i.InstalmentStatus === 'I')

  const collectedTotal = collected.reduce(
    (sum, i) => sum + (i.InstalmentAmount || 0),
    0
  )
  const nextCollection = scheduled[0]
  const lastScheduled = withDates[withDates.length - 1]
  const monthsOfRunway = lastScheduled
    ? (lastScheduled._date.getFullYear() - now.getFullYear()) * 12 +
      (lastScheduled._date.getMonth() - now.getMonth())
    : 0

  if (loading) {
    return <SkeletonWrapper loading={loading} />
  }

  return (
    <>
      {usingSampleData && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: '6px',
            background: '#FFF8E8',
            border: '1px solid #E0A11C',
            fontSize: '12px',
            color: '#6b5410'
          }}
        >
          {error
            ? `Could not load live RealPay data (${error}) — showing example layout.`
            : 'Showing example layout — no policy selected.'}
        </Box>
      )}

      {actionMessage && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: '6px',
            background:
              actionMessage.type === 'success' ? '#EAF7EE' : '#FDECEC',
            border:
              actionMessage.type === 'success'
                ? '1px solid #2D8D4D'
                : '1px solid #B23B3B',
            fontSize: '12px',
            color: actionMessage.type === 'success' ? '#1F6F3D' : '#B23B3B'
          }}
        >
          {actionMessage.text}
        </Box>
      )}

      {/* Header: contract status + actions */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            <StatusPill
              classes={classes}
              label={`Contract ${contractStatus.label}`}
              pillClass={contractStatus.pillClass}
            />
          </ul>
          <p style={{ margin: 0, fontSize: '12px', color: '#A9A9A9' }}>
            {contract.ContractNumber} &middot;{' '}
            {contract.FrequencyCode === 'MNTH'
              ? 'Monthly'
              : contract.FrequencyCode}
          </p>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            onClick={fetchData}
            disabled={!policyGuid || loading || actionLoading}
            style={{
              borderColor: '#FBA92D',
              color: '#000',
              textTransform: 'capitalize'
            }}
          >
            Refresh from RealPay
          </Button>
          {canAccess(['CAPTURE_POLICY']) && (
            <Button
              variant="contained"
              size="small"
              startIcon={<TrendingUpOutlinedIcon />}
              onClick={handleExtend}
              disabled={!policyGuid || usingSampleData || actionLoading}
              style={{
                background: '#FBA92D',
                color: '#000',
                textTransform: 'capitalize',
                boxShadow: 'none'
              }}
            >
              Extend Schedule
            </Button>
          )}
        </Box>
      </Box>

      {/* Client + contract summary cards */}
      <Grid container className={classes.policyWrapper}>
        <Grid item xs={12} sm={12} md={12}>
          <Grid container className={classes.policyHeader}>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sm={2}
                  className={classes.policyIconWrapper}
                >
                  <Box className={classes.policyIcons}>
                    <AccountBalanceOutlinedIcon
                      fontSize="14px"
                      sx={{ color: '#ffffff' }}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={10}
                  sm={10}
                  className={classes.policyHeaderContent}
                >
                  <h6>{client.ClientName || 'N/A'}</h6>
                  <p>Payer / Client</p>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container>
                <Grid item xs={12} md={6} sm={6}></Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sm={6}
                  className={classes.policyActiveStatusWrapper}
                >
                  <Box display="grid" justifyContent="flex-end">
                    <p>Bank</p>
                    <h6>{client.BankName || 'N/A'}</h6>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <div className={classes.policyHeaderBorder}></div>
          </Grid>

          <Grid container spacing={2} className={classes.policyContentWrapper}>
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={3}></Grid>
                <Grid item xs={12} sm={9} md={9}>
                  <p>Account Number</p>
                  <h6>{client.AccountNumber || 'N/A'}</h6>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p>Account Type</p>
              <h6>
                {ACCOUNT_TYPE_LABEL[client.AccountType] ||
                  client.AccountType ||
                  'N/A'}
              </h6>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p>Branch Code</p>
              <h6>{client.BranchCode || 'N/A'}</h6>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p>ID Number</p>
              <h6>{client.IDNumber || 'N/A'}</h6>
            </Grid>
          </Grid>

          <div className={classes.policyContentBorder}></div>

          <Grid container spacing={2} className={classes.policyFooterWrapper}>
            <Grid item xs={12} md={6} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4} sm={4}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3} md={3}></Grid>
                    <Grid item xs={12} sm={9} md={9}>
                      <p>Contract Number</p>
                      <h6>{contract.ContractNumber || 'N/A'}</h6>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} sm={4}>
                  <p>Instalment Amount</p>
                  <h6>{money(contract.FirstCollectionAmount)}</h6>
                </Grid>
                <Grid item xs={12} md={4} sm={4}>
                  <p>Number of Instalments</p>
                  <h6>{contract.NumberOfInstalments}</h6>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}></Grid>
                <Grid container xs={12} sm={6} md={6}>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={classes.policyBalance}>
                      <p style={{ textAlign: 'center' }}>First Collection</p>
                      <h6 style={{ textAlign: 'center' }}>
                        {formatDateLocale(contract.FirstCollectionDate) ||
                          'N/A'}
                      </h6>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={classes.policyBalance}>
                      <p style={{ textAlign: 'center' }}>Collection Day</p>
                      <h6 style={{ textAlign: 'center' }}>
                        {contract.CollectionDay === 99
                          ? 'Last day of month'
                          : contract.CollectionDay}
                      </h6>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Installment schedule — the part that actually matters day to day */}
      <Grid
        container
        spacing={2}
        className={classes.tabheading}
        style={{ marginTop: '30px' }}
      >
        <Grid item xs={12} sm={6} md={6}>
          <h1>Instalment Schedule</h1>
        </Grid>
      </Grid>

      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          background: '#fff'
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sm={2.4} md={2.4}>
            <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
              Collected
            </p>
            <h6 style={{ margin: '4px 0 0', fontSize: '14px' }}>
              {collected.length} &middot; {money(collectedTotal)}
            </h6>
          </Grid>
          <Grid item xs={6} sm={2.4} md={2.4}>
            <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
              Scheduled
            </p>
            <h6 style={{ margin: '4px 0 0', fontSize: '14px' }}>
              {scheduled.length}
            </h6>
          </Grid>
          <Grid item xs={6} sm={2.4} md={2.4}>
            <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
              Needs Attention
            </p>
            <h6
              style={{
                margin: '4px 0 0',
                fontSize: '14px',
                color: needsAttention.length ? '#8A6D1F' : 'inherit'
              }}
            >
              {needsAttention.length}
            </h6>
          </Grid>
          <Grid item xs={6} sm={2.4} md={2.4}>
            <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
              Next Collection
            </p>
            <h6 style={{ margin: '4px 0 0', fontSize: '14px' }}>
              {nextCollection
                ? formatDateLocale(nextCollection.InstalmentActionDate)
                : 'N/A'}
            </h6>
          </Grid>
          <Grid item xs={6} sm={2.4} md={2.4}>
            <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
              Runway
            </p>
            <h6
              style={{
                margin: '4px 0 0',
                fontSize: '14px',
                color: monthsOfRunway < 6 ? '#c53030' : 'inherit'
              }}
            >
              {monthsOfRunway} month{monthsOfRunway === 1 ? '' : 's'}
            </h6>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                #
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Collection Date
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Amount
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Status
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Reference
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Last Updated
              </TableCell>
              <TableCell
                style={{
                  fontFamily: 'Lato',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {withDates.map(instalment => {
              const status = getInstalmentStatusMeta(
                instalment.InstalmentStatus
              )
              return (
                <TableRow
                  key={
                    instalment.InstalmentReferenceNumber ||
                    instalment.InstalmentSequence
                  }
                >
                  <TableCell
                    style={{ fontFamily: 'Open Sans', fontSize: '12px' }}
                  >
                    {instalment.InstalmentSequence}
                  </TableCell>
                  <TableCell
                    style={{ fontFamily: 'Open Sans', fontSize: '12px' }}
                  >
                    {formatDateLocale(instalment.InstalmentActionDate) || 'N/A'}
                  </TableCell>
                  <TableCell
                    style={{ fontFamily: 'Open Sans', fontSize: '12px' }}
                  >
                    {money(instalment.InstalmentAmount)}
                  </TableCell>
                  <TableCell>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      <StatusPill
                        classes={classes}
                        label={status.label}
                        pillClass={status.pillClass}
                      />
                    </ul>
                  </TableCell>
                  <TableCell
                    style={{ fontFamily: 'Open Sans', fontSize: '12px' }}
                  >
                    {instalment.InstalmentReferenceNumber || 'N/A'}
                  </TableCell>
                  <TableCell
                    style={{ fontFamily: 'Open Sans', fontSize: '12px' }}
                  >
                    {formatDateLocale(instalment.LastUpdateDate) || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {instalment.InstalmentStatus === 'I' &&
                      canAccess(['CAPTURE_POLICY']) && (
                        <Button
                          size="small"
                          startIcon={<ReplayOutlinedIcon fontSize="small" />}
                          onClick={() => handleRetryInstalment(instalment)}
                          disabled={usingSampleData || actionLoading}
                          style={{
                            fontSize: '11px',
                            textTransform: 'capitalize',
                            color: '#8A6D1F'
                          }}
                        >
                          Retry
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {withDates.length === 0 && (
          <p style={{ textAlign: 'center', marginTop: '30px' }}>
            No instalments found
          </p>
        )}
      </Box>

      {cancelled.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#B23B3B' }} />
          <p style={{ margin: 0, fontSize: '11px', color: '#B23B3B' }}>
            {cancelled.length} instalment{cancelled.length === 1 ? '' : 's'} on
            this contract were cancelled by RealPay — use Retry on that row once
            the customer has confirmed they want it collected.
          </p>
        </Box>
      )}
    </>
  )
}

export default RealPayContract
